/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
    AlertTriangle, BadgeEuro, ClipboardList, Cpu, Download, Eye, FileText, Image,
    Package, RefreshCw, Search, Settings2, X,
} from "lucide-react";
import { Checkbox, CopyableField, ProcessSteps, SelectDropdown } from "../../components/ui/index.js";
import { pickCanonicalPackage } from "../../domain/digital/packages.js";

/**
 * DigitalProductsAdmin — revisión del producto digital (MVP-04A), con la misma
 * estructura visual que la administración de pedidos (AdminOrders): cabecera +
 * filtros, tarjeta de configuración arriba, grid lista/detalle, tabla zebra y
 * aside de detalle. En 04A ninguna versión es comprable.
 */

const DIGITAL_STATUSES = [
    ["pendiente_calculo", "Pendiente de cálculo"],
    ["calculando", "Calculando"],
    ["invalido", "Inválido / error"],
    ["pendiente_revision", "Pendiente de revisión"],
    ["aprobado", "Aprobado"],
    ["rechazado", "Rechazado"],
    ["obsoleto", "Obsoleto"],
];

const STATUS_BADGE = {
    pendiente_calculo: "badge-ghost",
    calculando: "badge-info",
    invalido: "badge-error",
    pendiente_revision: "badge-warning",
    aprobado: "badge-success",
    rechazado: "badge-error",
    obsoleto: "badge-ghost",
};

const PACKAGE_STATUS_BADGE = {
    pendiente: "badge-ghost",
    generando: "badge-info",
    generado: "badge-info",
    invalido: "badge-error",
    pendiente_revision: "badge-warning",
    aprobado: "badge-success",
    rechazado: "badge-error",
    obsoleto: "badge-ghost",
};

// Debe coincidir con `DigitalPackageVersion.TERMINAL_STATES` en el backend:
// un paquete en uno de estos estados es inmutable y no se reconstruye en
// sitio (Regenerar), solo se sustituye por una versión sucesora.
const PACKAGE_TERMINAL_STATES = new Set(["aprobado", "rechazado", "obsoleto"]);

const ACCESSORY_FIELDS = [
    ["has_magnet", "magnet_notes", "Imán"],
    ["has_nfc", "nfc_location_notes", "NFC"],
    ["has_whistle", "whistle_notes", "Silbato"],
    ["has_screws", "screw_notes", "Tornillos"],
];

// Debe coincidir con `_MIN_ACCESSORY_NOTE_LENGTH` en
// `CustomizedKeyring/digital/package_services.py`: es solo para dar el aviso
// en el propio formulario antes de guardar, no para relajar la validación
// real, que sigue viviendo en el backend.
const MIN_ACCESSORY_NOTE_LENGTH = 8;

function accessoryReviewIssues(form) {
    if (!form) return [];
    const issues = [];
    for (const [flagKey, notesKey, label] of ACCESSORY_FIELDS) {
        if (form[flagKey] && (form[notesKey] || "").trim().length < MIN_ACCESSORY_NOTE_LENGTH) {
            issues.push({ field: notesKey, label });
        }
    }
    if ((form.has_chain || form.has_ring || form.has_lanyard)
        && (form.accessory_notes || "").trim().length < MIN_ACCESSORY_NOTE_LENGTH) {
        issues.push({ field: "accessory_notes", label: "Cadena/anilla/leontina" });
    }
    return issues;
}

const PACKAGE_ARTIFACTS = [
    { kind: "model_3mf", manifestKey: "3mf", label: "Modelo 3MF", filename: "modelo.3mf", mode: "download", icon: Download },
    { kind: "pdf", manifestKey: "instructions_pdf", label: "Instrucciones PDF", filename: "instrucciones.pdf", mode: "preview", icon: FileText },
    { kind: "template_pdf", manifestKey: "template_pdf", label: "Plantilla PDF", filename: "plantilla-montaje.pdf", mode: "preview", icon: FileText },
    { kind: "template_svg", manifestKey: "template_svg", label: "Plantilla SVG", filename: "plantilla-montaje.svg", mode: "preview", icon: Image },
    { kind: "zip", manifestKey: "zip", label: "Paquete ZIP", filename: "pedido-digital.zip", mode: "download", icon: Download },
];

const fmt = (value, currency = "EUR") =>
    value == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(value || 0));

// Día, mes, año, hora y minuto, con la zona horaria local del navegador
// explícita (nunca solo la fecha, ni una hora sin decir en qué huso) —
// mismo formateador para la tabla, el detalle y la auditoría.
const dateTimeFormatter = new Intl.DateTimeFormat("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZoneName: "short",
});
const formatDateTime = (value) => (value ? dateTimeFormatter.format(new Date(value)) : "—");

const statusLabel = (status, fallback) =>
    fallback || DIGITAL_STATUSES.find(([value]) => value === status)?.[1] || status;

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    // Un backend caído/mal configurado puede devolver la página de error HTML
    // de Django en vez de JSON (por ejemplo un 500 no controlado). Pintar ese
    // cuerpo tal cual mostraría las etiquetas <!doctype html>... como texto
    // literal en la alerta, así que solo se usa como mensaje un string corto
    // que no parezca HTML.
    if (typeof data === "string") {
        const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(data);
        return (!looksLikeHtml && data.length < 300) ? data : fallback;
    }
    if (data.detail) return data.detail;
    return fallback;
};


function PriceConfigCard({ onChanged }) {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        base_keyring: "0.40", base_lightbox: "1.00",
        cost_per_extra_background: "0.25", cost_per_cm3: "0.01", activate: false,
    });

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get("/digital-price-configs/");
            setConfigs(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(formatApiError(err, "No se pudieron cargar las configuraciones."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const activate = async (id) => {
        try {
            await axios.post(`/digital-price-configs/${id}/activate/`, {});
            await load();
            onChanged?.();
        } catch (err) {
            setError(formatApiError(err, "No se pudo activar la configuración."));
        }
    };

    const create = async (event) => {
        event.preventDefault();
        setError("");
        try {
            await axios.post("/digital-price-configs/", form);
            await load();
            onChanged?.();
        } catch (err) {
            setError(formatApiError(err, "Configuración inválida."));
        }
    };

    const active = configs.find((c) => c.is_active);

    return (
        <details className="glassCard rounded-xl shrink-0 group">
            <summary className="p-3 flex items-center justify-between gap-3 cursor-pointer list-none">
                <div className="flex items-center gap-3">
                    <Settings2 className="w-6 h-6 text-primary" />
                    <div>
                        <h2 className="text-lg font-semibold">Configuración de precio</h2>
                        <p className="text-sm text-base-content/60">
                            {active ? `Activa: v${active.version}` : "Sin configuración activa"} · los coeficientes se versionan
                        </p>
                    </div>
                </div>
                <span className="btn btn-xs btn-ghost group-open:hidden">Editar precios</span>
                <span className="btn btn-xs btn-ghost hidden group-open:inline-flex">Ocultar</span>
            </summary>

            <div className="px-3 pb-3 flex flex-col gap-3 max-h-60 overflow-y-auto scrollbar_mod">

            {error && (
                <div role="alert" className="alert alert-error alert-sm text-sm">
                    <AlertTriangle className="w-4 h-4" /> {error}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="table table-sm">
                    <thead>
                        <tr>
                            <th>v</th><th>Activa</th><th>Keyring</th><th>Lightbox</th>
                            <th>+Bg</th><th>€/cm³</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center"><span className="loading loading-spinner loading-sm" /></td></tr>
                        ) : configs.map((c) => (
                            <tr key={c.id} className={c.is_active ? "bg-success/10" : ""}>
                                <td className="font-mono">{c.version}</td>
                                <td>{c.is_active && <span className="badge badge-success badge-sm">activa</span>}</td>
                                <td>{c.base_keyring}</td><td>{c.base_lightbox}</td>
                                <td>{c.cost_per_extra_background}</td><td>{c.cost_per_cm3}</td>
                                <td>{!c.is_active && <button className="btn btn-xs btn-outline" onClick={() => activate(c.id)}>Activar</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

                <form onSubmit={create} className="flex flex-wrap items-end gap-2">
                {[["base_keyring", "Base keyring"], ["base_lightbox", "Base lightbox"],
                  ["cost_per_extra_background", "+ background"], ["cost_per_cm3", "€/cm³"]].map(([k, label]) => (
                    <label key={k} className="form-control gap-1">
                        <span className="label-text text-xs">{label}</span>
                        <input
                            className="input input-bordered input-sm w-28"
                            value={form[k]}
                            inputMode="decimal"
                            onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                        />
                    </label>
                ))}
                <label className="label cursor-pointer gap-2 text-xs">
                    <input type="checkbox" className="checkbox checkbox-sm" checked={form.activate}
                           onChange={(e) => setForm((f) => ({ ...f, activate: e.target.checked }))} />
                    <span className="label-text text-xs">Activar al crear</span>
                </label>
                <button type="submit" className="btn btn-sm btn-primary">Crear versión</button>
                </form>
            </div>
        </details>
    );
}


function AccessoryReviewForm({ sourceVersionId, onConfirmed, onStatusChange }) {
    const [review, setReview] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`/digital-packages-admin/accessories/${sourceVersionId}/`);
            setReview(data);
            setForm(data);
            onStatusChange?.(data);
        } catch (err) {
            setError(formatApiError(err, "No se pudo cargar la revisión de accesorios."));
        } finally {
            setLoading(false);
        }
    }, [sourceVersionId, onStatusChange]);

    useEffect(() => { load(); }, [load]);

    if (loading || !form) {
        return <div className="flex justify-center py-4"><span className="loading loading-spinner loading-sm" /></div>;
    }

    const confirm = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await axios.put(`/digital-packages-admin/accessories/${sourceVersionId}/`, form);
            setReview(data);
            setForm(data);
            onStatusChange?.(data);
            onConfirmed?.(data);
        } catch (err) {
            setError(formatApiError(err, "No se pudo confirmar la revisión."));
        } finally {
            setSaving(false);
        }
    };

    const suggestion = review?.heuristic_suggestion || {};
    const suggestsInsert = Boolean(suggestion.possible_magnet_or_nfc);
    const suggestsScrews = Boolean(suggestion.possible_screws);
    const issues = accessoryReviewIssues(form);
    const issueFields = new Set(issues.map((i) => i.field));
    const hasIssues = issues.length > 0;

    return (
        <div className="flex flex-col gap-3">
            {error && <div role="alert" className="alert alert-error alert-sm text-xs">{error}</div>}
            {(suggestsInsert || suggestsScrews) && (
                <p className="text-[11px] text-info">
                    Sugerencia del heurístico (sin confirmar todavía):
                    {suggestsInsert && " posible imán o NFC"}
                    {suggestsInsert && suggestsScrews && " ·"}
                    {suggestsScrews && " posible tornillo"}
                </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {ACCESSORY_FIELDS.map(([flagKey, notesKey, label]) => (
                    <div key={flagKey} className="rounded-lg bg-base-200/60 p-2 flex flex-col gap-1">
                        <Checkbox
                            label={label}
                            checked={Boolean(form[flagKey])}
                            onChange={(v) => setForm((f) => ({ ...f, [flagKey]: v }))}
                        />
                        {form[flagKey] && (
                            <>
                                <input
                                    className={`input input-bordered input-xs ${issueFields.has(notesKey) ? "input-error" : ""}`}
                                    placeholder="Notas para el PDF (dimensiones/colocación/orientación)"
                                    value={form[notesKey] || ""}
                                    onChange={(e) => setForm((f) => ({ ...f, [notesKey]: e.target.value }))}
                                />
                                {issueFields.has(notesKey) && (
                                    <span className="text-[11px] text-error">
                                        Añade al menos {MIN_ACCESSORY_NOTE_LENGTH} caracteres para poder guardar.
                                    </span>
                                )}
                            </>
                        )}
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap gap-3">
                <Checkbox label="Cadena" checked={Boolean(form.has_chain)}
                          onChange={(v) => setForm((f) => ({ ...f, has_chain: v }))} />
                <Checkbox label="Anilla" checked={Boolean(form.has_ring)}
                          onChange={(v) => setForm((f) => ({ ...f, has_ring: v }))} />
                <Checkbox label="Leontina" checked={Boolean(form.has_lanyard)}
                          onChange={(v) => setForm((f) => ({ ...f, has_lanyard: v }))} />
            </div>
            <div className="flex flex-col gap-1">
                <textarea
                    className={`textarea textarea-bordered textarea-xs ${issueFields.has("accessory_notes") ? "textarea-error" : ""}`}
                    placeholder="Notas adicionales de montaje"
                    value={form.accessory_notes || ""}
                    onChange={(e) => setForm((f) => ({ ...f, accessory_notes: e.target.value }))}
                />
                {issueFields.has("accessory_notes") && (
                    <span className="text-[11px] text-error">
                        Cadena, anilla o leontina necesitan una especificación aquí (mínimo {MIN_ACCESSORY_NOTE_LENGTH} caracteres).
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2">
                <button className="btn btn-xs btn-primary" disabled={saving || hasIssues} onClick={confirm}
                        title={hasIssues ? "Completa las notas marcadas en rojo antes de guardar" : undefined}>
                    {review?.is_confirmed ? "Actualizar revisión" : "Confirmar revisión"}
                </button>
                {review?.is_confirmed && <span className="badge badge-success badge-sm">confirmada</span>}
                {hasIssues && (
                    <span className="text-[11px] text-error">
                        Falta información en: {issues.map((i) => i.label).join(", ")}.
                    </span>
                )}
            </div>
        </div>
    );
}

function PackageSection({ sourceVersionId }) {
    const [pkg, setPkg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reason, setReason] = useState("");
    const [activeStep, setActiveStep] = useState("accessories");
    const [accessoriesConfirmed, setAccessoriesConfirmed] = useState(false);
    const [artifactLoading, setArtifactLoading] = useState("");
    const [artifactPreview, setArtifactPreview] = useState(null);
    // La licencia ya no se teclea: se selecciona la versión activa real
    // (MVP-02B §6). El backend revalida que siga siendo la activa al aprobar.
    const [activeLicense, setActiveLicense] = useState(undefined); // undefined=cargando

    const loadActiveLicense = useCallback(async () => {
        setActiveLicense(undefined);
        try {
            const { data } = await axios.get("/legal/active/");
            setActiveLicense(data?.digital_license ?? null);
        } catch {
            setActiveLicense(null);
        }
    }, []);

    useEffect(() => {
        loadActiveLicense();
        // Si la licencia se activa desde otra pestaña, al volver al panel debe
        // aparecer sin exigir una recarga completa de la aplicación.
        const refreshOnFocus = () => loadActiveLicense();
        window.addEventListener("focus", refreshOnFocus);
        return () => window.removeEventListener("focus", refreshOnFocus);
    }, [loadActiveLicense]);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get("/digital-packages-admin/", {
                params: { source_version: sourceVersionId },
            });
            // Selección determinista del paquete vigente: la API puede devolver
            // varios intentos para la misma versión fuente y `data[0]` podía
            // enseñar uno antiguo (MVP-02B §6).
            setPkg(pickCanonicalPackage(data, sourceVersionId));
        } catch (err) {
            setError(formatApiError(err, "No se pudo cargar el paquete."));
        } finally {
            setLoading(false);
        }
    }, [sourceVersionId]);

    useEffect(() => {
        load();
        setReason("");
        setActiveStep("accessories");
        setAccessoriesConfirmed(false);
    }, [load]);

    useEffect(() => () => {
        if (artifactPreview?.url) window.URL.revokeObjectURL(artifactPreview.url);
    }, [artifactPreview]);

    const handleAccessoryStatus = useCallback((review) => {
        setAccessoriesConfirmed(Boolean(review?.is_confirmed));
    }, []);

    const runPackageAction = async (action, body) => {
        setError("");
        try {
            const url = action === "generate" || !pkg
                ? `/digital-packages-admin/generate/${sourceVersionId}/`
                : `/digital-packages-admin/${pkg.id}/${action}/`;
            const { data } = await axios.post(url, body || {});
            setPkg(data);
            if (["generate", "retry"].includes(action) && data?.artifact_manifest?.instructions_pdf) {
                setActiveStep("files");
            }
        } catch (err) {
            setError(formatApiError(err, "No se pudo completar la acción del paquete."));
        }
    };

    const openArtifact = async ({ kind, filename, mode }) => {
        if (!pkg) return;

        setArtifactLoading(kind);
        setError("");
        try {
            const response = await axios.get(`/digital-packages-admin/${pkg.id}/preview/${kind}/`, {
                responseType: "blob",
            });
            const objectUrl = window.URL.createObjectURL(response.data);

            if (mode === "preview") {
                setArtifactPreview({ url: objectUrl, filename });
            } else {
                const link = document.createElement("a");
                link.href = objectUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(objectUrl);
            }
        } catch (err) {
            setError(formatApiError(err, `No se pudo abrir ${filename}.`));
        } finally {
            setArtifactLoading("");
        }
    };

    const artifacts = PACKAGE_ARTIFACTS.filter(({ manifestKey }) => pkg?.artifact_manifest?.[manifestKey]);
    const packageState = pkg?.state_display || pkg?.state;
    const isTerminalPackageState = PACKAGE_TERMINAL_STATES.has(pkg?.state);

    const licenseAssigned = Boolean(pkg?.required_license_version);
    const licenseMatchesActive = Boolean(
        activeLicense && pkg?.required_license_version === activeLicense.version_label,
    );

    const steps = [
        { id: "accessories", label: "1. Accesorios", done: accessoriesConfirmed },
        { id: "generation", label: "2. Generación", done: Boolean(pkg) && !["pendiente", "generando", "invalido"].includes(pkg.state) },
        { id: "files", label: "3. Archivos", done: artifacts.length > 0 },
    ];

    /**
     * Recorrido completo de administración (MVP-02B §7): cada paso dice si está
     * completado, en curso o bloqueado, por qué y qué hacer a continuación.
     */
    const buildAdminJourney = () => {
        const priceApproved = true; // esta sección solo se monta con precio aprobado
        const filesReady = artifacts.length > 0;
        const packageApproved = pkg?.state === "aprobado";

        const paso = (done, current) => (done ? "done" : current ? "current" : "pending");

        return [
            {
                id: "precio",
                label: "Precio aprobado",
                state: "done",
            },
            {
                id: "accesorios",
                label: "Accesorios confirmados",
                state: paso(accessoriesConfirmed, priceApproved && !accessoriesConfirmed),
                actor: accessoriesConfirmed ? undefined : "Confirma cadena, anilla y acabado.",
                action: accessoriesConfirmed
                    ? undefined
                    : { label: "Ir a accesorios", onClick: () => setActiveStep("accessories") },
            },
            {
                id: "archivos",
                label: "Archivos generados",
                state: paso(filesReady, accessoriesConfirmed && !filesReady),
                reason: pkg?.last_error ? `Error de generación: ${pkg.last_error}` : undefined,
                action: filesReady || !accessoriesConfirmed
                    ? undefined
                    : { label: "Ir a generación", onClick: () => setActiveStep("generation") },
            },
            {
                id: "licencia",
                label: "Licencia asignada",
                state: licenseMatchesActive
                    ? "done"
                    : activeLicense === null
                      ? "blocked"
                      : paso(false, filesReady),
                description: licenseMatchesActive ? pkg.required_license_version : undefined,
                reason: activeLicense === undefined
                    ? "Comprobando la licencia digital activa…"
                    : activeLicense === null
                      ? "No hay ninguna versión de licencia digital activa."
                    : !pkg
                      ? `La licencia activa «${activeLicense.version_label}» está lista. Genera primero el paquete para poder asignarla.`
                      : !filesReady
                        ? `La licencia activa «${activeLicense.version_label}» se podrá asignar cuando termine la generación.`
                    : licenseAssigned && !licenseMatchesActive
                      ? "La licencia del paquete ya no es la versión activa."
                      : !licenseMatchesActive
                        ? `Asigna la licencia activa «${activeLicense.version_label}» desde el paso Generación.`
                      : undefined,
                action: activeLicense === undefined
                    ? undefined
                    : activeLicense === null
                      ? { label: "Configurar licencia", onClick: () => { window.location.href = "/admin/legal"; } }
                    : pkg && filesReady && !licenseMatchesActive
                      ? { label: "Asignar licencia", onClick: () => setActiveStep("generation") }
                    : undefined,
            },
            {
                id: "aprobacion",
                label: "Aprobación final",
                state: packageApproved
                    ? "done"
                    : !licenseMatchesActive
                      ? "blocked"
                      : paso(false, pkg?.state === "pendiente_revision"),
                reason: !packageApproved && !licenseMatchesActive
                    ? "Requiere la licencia activa asignada."
                    : undefined,
                description: packageApproved
                    ? "El propietario ya puede añadirlo al carrito."
                    : undefined,
            },
        ];
    };

    return (
        <>
        <section className="flex flex-col gap-3">
            <h4 className="font-semibold mb-2 text-sm flex items-center gap-1.5">
                <Package className="w-4 h-4" /> Paquete comercial
            </h4>

            <div className="rounded-lg border border-base-300 bg-base-200/40 p-3">
                <h5 className="text-[11px] uppercase tracking-widest text-base-content/50 font-semibold mb-3">
                    Recorrido del paquete
                </h5>
                <ProcessSteps compact steps={buildAdminJourney()} />
            </div>

            <div role="tablist" aria-label="Preparación del paquete" className="tabs tabs-boxed grid grid-cols-3">
                {steps.map((step) => (
                    <button
                        key={step.id}
                        type="button"
                        role="tab"
                        aria-selected={activeStep === step.id}
                        className={`tab h-auto min-h-9 px-2 text-[11px] gap-1 ${activeStep === step.id ? "tab-active" : ""}`}
                        onClick={() => setActiveStep(step.id)}
                    >
                        {step.done && <span className="text-success" aria-label="completado">●</span>}
                        {step.label}
                    </button>
                ))}
            </div>

            {error && <div role="alert" className="alert alert-error alert-sm text-xs mb-2">{error}</div>}

            {activeStep === "accessories" && (
                <div className="rounded-lg bg-base-200/40 p-3">
                    <p className="text-xs text-base-content/60 mb-2">
                        Confirma qué necesita el montaje. Esta información alimenta el PDF.
                    </p>
                    <AccessoryReviewForm
                        sourceVersionId={sourceVersionId}
                        onStatusChange={handleAccessoryStatus}
                        onConfirmed={() => {
                            load();
                            setActiveStep("generation");
                        }}
                    />
                </div>
            )}

            {activeStep === "generation" && (
                <div className="rounded-lg bg-base-200/40 p-3 flex flex-col gap-3">
                    {loading ? (
                        <div className="flex justify-center py-3"><span className="loading loading-spinner loading-sm" /></div>
                    ) : !pkg ? (
                        <div className="flex flex-col items-start gap-2">
                            <span className="text-xs text-base-content/60">
                                {accessoriesConfirmed
                                    ? "La revisión está confirmada. Ya puedes construir el paquete."
                                    : "Confirma primero la revisión de accesorios."}
                            </span>
                            <button className="btn btn-xs btn-primary gap-1" disabled={!accessoriesConfirmed}
                                    onClick={() => runPackageAction("generate")}>
                                <RefreshCw className="w-3 h-3" /> Generar paquete
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`badge badge-sm ${PACKAGE_STATUS_BADGE[pkg.state] || "badge-ghost"}`}>
                                    {packageState}
                                </span>
                                {pkg.has_template && <span className="badge badge-ghost badge-sm">con plantilla</span>}
                                <span className="text-[11px] text-base-content/50">{artifacts.length} archivos listos</span>
                            </div>

                            {pkg.last_error && <p className="text-xs text-error">Error: {pkg.last_error}</p>}
                            {pkg.approval?.rejection_reason && (
                                <p className="text-xs text-error">Rechazo: {pkg.approval.rejection_reason}</p>
                            )}

                            <CopyableField label="Clave canónica" value={pkg.canonical_key} mono />
                            <CopyableField label="Referencia de licencia" value={pkg.required_license_version}
                                           emptyText="Sin licencia registrada (pendiente D-05/MVP-08)" />

                            <div className="order-2 flex flex-wrap gap-2">
                                <button className="btn btn-xs btn-outline gap-1"
                                        disabled={isTerminalPackageState}
                                        title={isTerminalPackageState
                                            ? "Un paquete aprobado/rechazado/obsoleto no se reconstruye; genera una nueva versión cambiando accesorios u origen."
                                            : "Vuelve a exportar el 3MF, el PDF y las plantillas para este mismo paquete, aunque nada haya cambiado."}
                                        onClick={() => runPackageAction("retry")}>
                                    <RefreshCw className="w-3 h-3" />
                                    {pkg.state === "invalido" ? "Reintentar" : "Regenerar"}
                                </button>
                                <button className="btn btn-xs btn-success"
                                        disabled={pkg.state !== "pendiente_revision" || !licenseMatchesActive}
                                        title={!licenseMatchesActive
                                            ? "Asigna primero la licencia digital activa en el bloque anterior."
                                            : undefined}
                                        onClick={() => runPackageAction("approve")}>
                                    Aprobar paquete
                                </button>
                            </div>
                            <div className="order-3 flex flex-wrap items-end gap-2">
                                <input className="input input-bordered input-xs w-48" placeholder="Motivo (rechazo/obsoleto)"
                                       value={reason} onChange={(e) => setReason(e.target.value)} />
                                <button className="btn btn-xs btn-error" disabled={!reason.trim()}
                                        onClick={() => runPackageAction("reject", { reason })}>Rechazar</button>
                                <button className="btn btn-xs btn-ghost" disabled={!reason.trim()}
                                        onClick={() => runPackageAction("obsolete", { reason })}>Obsoletar</button>
                            </div>
                            {/* Licencia: selección de la versión activa real, no texto libre */}
                            <div className="order-1 rounded-lg border border-base-300 bg-base-100/40 p-2 flex flex-col gap-2">
                                {activeLicense === undefined ? (
                                    <p className="text-[11px] text-base-content/50">Comprobando la licencia activa…</p>
                                ) : activeLicense === null ? (
                                    <>
                                        <p className="text-[11px] text-warning flex items-center gap-1">
                                            <AlertTriangle className="w-3 h-3 shrink-0" />
                                            No hay ninguna licencia digital activa configurada.
                                        </p>
                                        <a href="/admin/legal" className="btn btn-xs btn-outline self-start">
                                            Configurar licencia
                                        </a>
                                        <button type="button" className="btn btn-xs btn-ghost self-start"
                                                onClick={loadActiveLicense}>
                                            Volver a comprobar
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-[11px] text-base-content/60">Licencia activa:</span>
                                            <span className="badge badge-sm badge-ghost">{activeLicense.version_label}</span>
                                            {activeLicense.reference_url && (
                                                <a href={activeLicense.reference_url} target="_blank" rel="noreferrer"
                                                   className="link link-primary text-[11px]">
                                                    Ver documento
                                                </a>
                                            )}
                                        </div>
                                        <button
                                            className="btn btn-xs btn-outline self-start"
                                            disabled={pkg.state === "aprobado" || licenseMatchesActive}
                                            title={pkg.state === "aprobado"
                                                ? "Un paquete aprobado es inmutable."
                                                : licenseMatchesActive
                                                  ? "El paquete ya tiene asignada la licencia activa."
                                                  : undefined}
                                            onClick={() => runPackageAction("set_license", {
                                                license_version: activeLicense.version_label,
                                            })}
                                        >
                                            {licenseMatchesActive ? "Licencia asignada" : "Asignar licencia activa"}
                                        </button>
                                        {licenseAssigned && !licenseMatchesActive && (
                                            <p className="text-[11px] text-warning">
                                                El paquete tiene «{pkg.required_license_version}», que ya no es la versión
                                                activa. Reasígnala antes de aprobar.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeStep === "files" && (
                <div className="rounded-lg bg-base-200/40 p-3 flex flex-col gap-3">
                    {artifacts.length === 0 ? (
                        <div className="text-xs text-base-content/60">
                            Todavía no hay archivos. Completa la generación o revisa su error en el paso anterior.
                        </div>
                    ) : (
                        <>
                            <p className="text-xs text-base-content/60">
                                Los PDF y SVG se abren en la vista previa integrada; el 3MF y el ZIP se descargan.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {artifacts.map((artifact) => {
                                    const Icon = artifact.icon;
                                    return (
                                        <button
                                            key={artifact.kind}
                                            type="button"
                                            className="btn btn-sm btn-outline justify-start gap-2"
                                            disabled={Boolean(artifactLoading)}
                                            onClick={() => openArtifact(artifact)}
                                        >
                                            {artifactLoading === artifact.kind
                                                ? <span className="loading loading-spinner loading-xs" />
                                                : <Icon className="w-4 h-4" />}
                                            {artifact.mode === "preview" ? "Abrir" : "Descargar"} {artifact.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </section>
        {artifactPreview && typeof document !== "undefined" && createPortal(
            <div
                role="dialog"
                aria-modal="true"
                aria-label={`Vista previa de ${artifactPreview.filename}`}
                className="fixed inset-0 z-[100] bg-black/75 p-3 md:p-8 flex items-center justify-center"
            >
                <div className="glassCard rounded-xl w-full h-full max-w-6xl flex flex-col overflow-hidden">
                    <div className="p-3 border-b border-base-300 flex items-center justify-between gap-3">
                        <div>
                            <h4 className="font-semibold">Vista previa</h4>
                            <p className="text-xs text-base-content/60">{artifactPreview.filename}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <a
                                href={artifactPreview.url}
                                download={artifactPreview.filename}
                                className="btn btn-sm btn-outline gap-1"
                            >
                                <Download className="w-4 h-4" /> Descargar
                            </a>
                            <button
                                type="button"
                                className="btn btn-sm btn-ghost btn-circle"
                                aria-label="Cerrar vista previa"
                                onClick={() => setArtifactPreview(null)}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                    <iframe
                        title={`Vista previa de ${artifactPreview.filename}`}
                        src={artifactPreview.url}
                        className="w-full flex-1 bg-white"
                    />
                </div>
            </div>,
            document.body,
        )}
        </>
    );
}

function VersionDetail({ version, loading, onClose, onAction }) {
    const [reason, setReason] = useState("");
    const [overridePrice, setOverridePrice] = useState("");

    useEffect(() => { setReason(""); setOverridePrice(""); }, [version?.id]);

    if (!version && !loading) {
        return (
            <aside className="glassCard rounded-xl p-4 min-h-80 flex flex-col items-center justify-center gap-2 text-center text-base-content/60">
                <Cpu className="w-10 h-10 opacity-30" />
                <p>Selecciona una versión para ver el detalle.</p>
            </aside>
        );
    }

    return (
        <aside className="glassCard rounded-xl p-4 flex flex-col gap-4 min-w-0">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    {version?.design_thumbnail ? (
                        <img
                            src={version.design_thumbnail}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover shrink-0 bg-base-200"
                        />
                    ) : (
                        <span className="w-12 h-12 rounded-lg shrink-0 bg-base-200 grid place-content-center">
                            <Image className="w-5 h-5 opacity-40" aria-hidden />
                        </span>
                    )}
                    <div className="min-w-0">
                        <h3 className="font-bold text-lg break-words">{version?.design_name || `Versión #${version?.id}`}</h3>
                        <p className="text-xs text-base-content/55 break-words">
                            diseño #{version?.design_id} · versión #{version?.id} · {version?.owner_email || `usuario #${version?.owner_id}`}
                        </p>
                        <p className="text-xs text-base-content/55">
                            {version?.product_type} · {formatDateTime(version?.created_at)}
                        </p>
                    </div>
                </div>
                <button className="btn btn-sm btn-ghost btn-circle shrink-0" onClick={onClose}>
                    <X className="w-4 h-4" />
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <span className="loading loading-spinner loading-lg text-primary" />
                </div>
            ) : (
                <>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`badge ${STATUS_BADGE[version.state] || "badge-ghost"}`}>
                            {statusLabel(version.state, version.state_display)}
                        </span>
                        <span className="badge badge-ghost badge-sm">no comprable</span>
                        {version.supersedes != null && (
                            <span className="badge badge-outline badge-sm" title="Un cambio real de medición/precio crea una versión sucesora en vez de reescribir la anterior">
                                sucesora de la versión #{version.supersedes}
                            </span>
                        )}
                    </div>

                    <section className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-base-200/60 p-3">
                            <p className="text-xs text-base-content/55 mb-1">Medición</p>
                            <div className="flex justify-between"><span>Backgrounds</span><span>{version.background_count ?? "—"}</span></div>
                            <div className="flex justify-between"><span>Volumen cm³</span><span className="font-mono">{version.volume_cm3 ?? "—"}</span></div>
                            <div className="flex justify-between"><span>Volumen mm³</span><span className="font-mono text-xs">{version.volume_mm3 ?? "—"}</span></div>
                            <div className="flex justify-between text-xs text-base-content/50"><span>Motor</span><span>{version.measurement_engine || "—"}</span></div>
                        </div>
                        <div className="rounded-lg bg-base-200/60 p-3">
                            <p className="text-xs text-base-content/55 mb-1">Precio</p>
                            <div className="flex justify-between"><span>Calculado</span><span>{fmt(version.calculated_price, version.currency)}</span></div>
                            <div className="flex justify-between font-semibold"><span>Final revisado</span><span>{fmt(version.final_price, version.currency)}</span></div>
                            {version.override?.reason && (
                                <p className="text-[11px] text-base-content/50 mt-1">Override: {version.override.reason}</p>
                            )}
                        </div>
                    </section>

                    {version.approval?.rejection_reason && (
                        <p className="text-xs text-error">Rechazo: {version.approval.rejection_reason}</p>
                    )}

                    <section>
                        <h4 className="font-semibold mb-2 text-sm">Revisión</h4>
                        <div className="flex flex-wrap gap-2">
                            <button className="btn btn-xs btn-success" disabled={version.state !== "pendiente_revision"}
                                    onClick={() => onAction(version.id, "approve")}>Aprobar</button>
                            <button className="btn btn-xs" onClick={() => onAction(version.id, "recalculate")}>Recalcular</button>
                        </div>
                        <div className="mt-2 flex flex-wrap items-end gap-2">
                            <input className="input input-bordered input-xs w-48" placeholder="Motivo (rechazo/obsoleto)"
                                   value={reason} onChange={(e) => setReason(e.target.value)} />
                            <button className="btn btn-xs btn-error" disabled={!reason.trim()}
                                    onClick={() => onAction(version.id, "reject", { reason })}>Rechazar</button>
                            <button className="btn btn-xs btn-ghost" disabled={!reason.trim()}
                                    onClick={() => onAction(version.id, "obsolete", { reason })}>Obsoletar</button>
                        </div>
                        <div className="mt-2 flex flex-wrap items-end gap-2">
                            <input className="input input-bordered input-xs w-24" placeholder="Precio €" inputMode="decimal"
                                   value={overridePrice} onChange={(e) => setOverridePrice(e.target.value)} />
                            <button className="btn btn-xs btn-outline" disabled={!overridePrice.trim() || !reason.trim()}
                                    onClick={() => onAction(version.id, "override", { final_price: overridePrice, reason })}>
                                Override precio
                            </button>
                            <span className="text-[11px] text-base-content/40">requiere motivo</span>
                        </div>
                    </section>

                    <PackageSection sourceVersionId={version.id} />

                    {Array.isArray(version.events) && version.events.length > 0 && (
                        <details className="shrink-0 rounded-lg border border-base-300 bg-base-200/30 p-3">
                            <summary className="cursor-pointer select-none font-semibold text-sm">
                                Auditoría ({version.events.length})
                            </summary>
                            {/* El bloque es desplegable y no puede encogerse a altura cero.
                                Antes `min-h-0` dentro del aside flex hacía que sus hijos
                                desbordaran por encima de accesorios, generación y archivos. */}
                            <ul className="mt-3 flex flex-col gap-1.5 text-xs text-base-content/70 max-h-64 overflow-y-auto scrollbar_mod pr-1">
                                {version.events.map((e) => (
                                    <li key={e.id ?? `${e.action}-${e.at}`}
                                        className="flex flex-col gap-0.5 border-b border-base-300/50 pb-1.5">
                                        <span className="break-words">
                                            <span className="font-medium">{e.action}</span>
                                            {e.from && ` (${e.from}→${e.to})`}
                                            {e.reason ? ` · ${e.reason}` : ""}
                                        </span>
                                        <span className="text-base-content/40 flex flex-wrap justify-between gap-2">
                                            <span>{e.actor_email || (e.actor ? `usuario #${e.actor}` : "sistema")}</span>
                                            <span className="whitespace-nowrap">{formatDateTime(e.at)}</span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}
        </aside>
    );
}


export default function DigitalProductsAdmin() {
    const [versions, setVersions] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);
    const [selectedLoading, setSelectedLoading] = useState(false);

    const fetchVersions = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get("/digital-products-admin/", {
                params: { state: statusFilter || undefined },
            });
            setVersions(Array.isArray(data) ? data : []);
        } catch (err) {
            setError(formatApiError(err, "No se pudieron cargar los candidatos digitales."));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { fetchVersions(); }, [fetchVersions]);

    const openVersion = async (id) => {
        setSelectedLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`/digital-products-admin/${id}/`);
            setSelected(data);
        } catch (err) {
            setError(formatApiError(err, "No se pudo cargar el detalle."));
        } finally {
            setSelectedLoading(false);
        }
    };

    const runAction = async (id, action, body) => {
        setError("");
        try {
            const { data } = await axios.post(`/digital-products-admin/${id}/${action}/`, body || {});
            setSelected(data);
            await fetchVersions();
        } catch (err) {
            setError(formatApiError(err, "No se pudo completar la acción."));
        }
    };

    const filtered = search.trim()
        ? versions.filter((v) => {
            const q = search.trim().toLowerCase();
            return String(v.id).includes(q)
                || String(v.design_id || "").includes(q)
                || String(v.owner_id || "").includes(q)
                || (v.design_name || "").toLowerCase().includes(q)
                || (v.owner_email || "").toLowerCase().includes(q)
                || (v.product_type || "").toLowerCase().includes(q);
        })
        : versions;

    return (
        <div className="flex flex-col gap-3">
            <PriceConfigCard onChanged={fetchVersions} />

            <div className="glassCard rounded-xl p-3 flex flex-col gap-3 shrink-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <BadgeEuro className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Producto digital</h2>
                            <p className="text-sm text-base-content/60">{filtered.length} candidatos · no comprables en MVP-04A</p>
                        </div>
                    </div>
                    <button className="btn btn-sm btn-outline gap-2" onClick={fetchVersions}>
                        <RefreshCw className="w-4 h-4" />
                        Actualizar
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                    <label className="input input-bordered input-sm flex items-center gap-2">
                        <Search className="w-4 h-4 opacity-60" />
                        <input
                            className="grow"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="ID, diseño, propietario, tipo"
                        />
                    </label>
                    <SelectDropdown
                        size="sm"
                        placeholder="Estado"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={[
                            { value: "", label: "Todos" },
                            ...DIGITAL_STATUSES.map(([value, label]) => ({ value, label })),
                        ]}
                    />
                </div>
            </div>

            {error && (
                <div role="alert" className="alert alert-error shadow-lg shrink-0">
                    <AlertTriangle className="w-5 h-5" />
                    <div>
                        <h3 className="font-semibold">Hay un problema con el producto digital</h3>
                        <div className="text-sm">{error}</div>
                    </div>
                    <button className="btn btn-sm btn-ghost" onClick={fetchVersions}>Reintentar</button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_520px] gap-3 items-start">
                <div className="glassCard rounded-xl min-w-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <span className="loading loading-spinner loading-lg text-primary" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full gap-2 text-center text-base-content/60">
                            <ClipboardList className="w-10 h-10 opacity-30" />
                            <p>No hay candidatos con estos filtros.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>Diseño</th>
                                        <th>#</th>
                                        <th>Tipo</th>
                                        <th>Medición</th>
                                        <th>Estado</th>
                                        <th className="text-right">Precio</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((v) => (
                                        <tr key={v.id} className={selected?.id === v.id ? "bg-primary/10" : ""}>
                                            <td>
                                                <div className="flex items-center gap-2 max-w-[16rem]">
                                                    {v.design_thumbnail ? (
                                                        <img
                                                            src={v.design_thumbnail}
                                                            alt=""
                                                            className="w-8 h-8 rounded object-cover shrink-0 bg-base-200"
                                                        />
                                                    ) : (
                                                        <span className="w-8 h-8 rounded shrink-0 bg-base-200 grid place-content-center">
                                                            <Image className="w-4 h-4 opacity-40" aria-hidden />
                                                        </span>
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-medium truncate" title={v.design_name}>
                                                            {v.design_name || "—"}
                                                        </div>
                                                        <div className="text-[11px] text-base-content/50 truncate">
                                                            diseño #{v.design_id} · {v.owner_email || `usuario #${v.owner_id}`}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="font-mono font-semibold">#{v.id}</div>
                                                <div className="text-xs text-base-content/50">
                                                    {formatDateTime(v.created_at)}
                                                </div>
                                            </td>
                                            <td>{v.product_type}</td>
                                            <td className="text-xs">
                                                <div>{v.background_count ?? "—"} bg</div>
                                                <div className="font-mono text-base-content/60">{v.volume_cm3 ?? "—"} cm³</div>
                                            </td>
                                            <td>
                                                <span className={`badge badge-sm ${STATUS_BADGE[v.state] || "badge-ghost"}`}>
                                                    {statusLabel(v.state, v.state_display)}
                                                </span>
                                            </td>
                                            <td className="text-right font-semibold">{fmt(v.final_price, v.currency)}</td>
                                            <td>
                                                <button className="btn btn-xs btn-outline gap-1" onClick={() => openVersion(v.id)}>
                                                    <Eye className="w-3 h-3" />
                                                    Ver
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <VersionDetail
                    version={selected}
                    loading={selectedLoading}
                    onClose={() => setSelected(null)}
                    onAction={runAction}
                />
            </div>
        </div>
    );
}
